import type { ErrorCode } from '@avrahami/contracts';
import { isApiError } from './api';

/**
 * Dates and times are shown in a given IANA time zone (a store's own zone in the tenant admin);
 * without one, in the browser's zone. Formatters are cached per zone.
 */
const formatters = new Map<string, Intl.DateTimeFormat>();

function formatter(kind: 'dateTime' | 'date', timeZone: string | undefined): Intl.DateTimeFormat {
  const key = `${kind}|${timeZone ?? ''}`;
  let found = formatters.get(key);
  if (found === undefined) {
    found = new Intl.DateTimeFormat('he-IL', {
      ...(kind === 'dateTime'
        ? { dateStyle: 'short', timeStyle: 'short' }
        : { dateStyle: 'medium' }),
      ...(timeZone === undefined ? {} : { timeZone }),
    });
    formatters.set(key, found);
  }
  return found;
}

export function formatDateTime(iso: string, timeZone?: string): string {
  return formatter('dateTime', timeZone).format(new Date(iso));
}

export function formatDate(iso: string, timeZone?: string): string {
  return formatter('date', timeZone).format(new Date(iso));
}

/** A short, readable form of an id for tables (the full id stays in the title attribute). */
export function shortId(id: string): string {
  return id.slice(-8);
}

const TOKEN_PATTERN = /^[A-Za-z0-9_-]{43}$/;

/**
 * The link a person opens to set their password. The token sits in the fragment, which browsers
 * never send to servers, so it stays out of access logs and `Referer` headers.
 */
export function invitationLink(origin: string, token: string): string {
  return `${origin.replace(/\/+$/, '')}/invitation#${token}`;
}

/** The token from a URL fragment, if it has the right shape. */
export function tokenFromHash(hash: string): string | undefined {
  const token = hash.startsWith('#') ? hash.slice(1) : hash;
  return TOKEN_PATTERN.test(token) ? token : undefined;
}

/** The words an app gives for API errors: one per code, plus precise server messages. */
export interface ErrorDictionary {
  errors: Readonly<Record<ErrorCode, string>>;
  serverMessages: Readonly<Record<string, string>>;
}

/** A message in the app's language for any error the UI shows. */
export function translateError(error: unknown, dictionary: ErrorDictionary): string {
  if (isApiError(error)) {
    return dictionary.serverMessages[error.message] ?? dictionary.errors[error.code];
  }
  return dictionary.errors.INTERNAL;
}

export type ErrorOverrides = Partial<Readonly<Record<ErrorCode, string>>>;

/**
 * An app's `errorMessage(error, overrides?)`: its dictionary, plus what a code means on one
 * screen (a used coupon answers CONFLICT, for example).
 */
export function createErrorMessage(dictionary: ErrorDictionary) {
  return (error: unknown, overrides?: ErrorOverrides): string => {
    if (isApiError(error)) {
      const override = overrides?.[error.code];
      if (override !== undefined) return override;
    }
    return translateError(error, dictionary);
  };
}

/** Field names the server rejected (the last segment of each detail path). */
export function invalidFields(error: unknown): Set<string> {
  if (!isApiError(error, 'VALIDATION_FAILED')) return new Set();
  return new Set(error.details.map((detail) => detail.path.split('/').at(-1) ?? ''));
}

/**
 * Where to go after signing in. Only a path on this site is accepted: anything else (another
 * origin, a protocol-relative `//host`, a backslash trick) falls back to `home`, so the sign-in
 * page cannot be used as an open redirect.
 */
export function safeRedirect(target: string | undefined, home: string): string {
  if (target === undefined || !target.startsWith('/') || target.startsWith('//')) return home;
  if (target.includes('\\') || target.startsWith('/login')) return home;
  return target;
}

/** Fills `{name}` placeholders of a dictionary string. */
export function fill(text: string, values: Readonly<Record<string, string | number>>): string {
  return text.replace(/\{(\w+)\}/g, (whole, key: string) =>
    key in values ? String(values[key]) : whole,
  );
}

/** A count in words: Hebrew says "one product" differently from "{count} products". */
export function countText(count: number, words: { one: string; many: string }): string {
  return count === 1 ? words.one : fill(words.many, { count });
}
